import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
} from 'recharts';

const CATEGORY_LABEL_MAP: Record<string, string> = {
    advertising: 'Ad Spend',
    discount: 'Discounts',
    product_return: 'Returns',
    partial_return: 'Returns',
    transaction_fee: 'Payment Fees',
    shipping: 'Shipping',
    other: 'Other',
};

const CATEGORY_COLOR_MAP: Record<string, string> = {
    'Ad Spend': CHART_COLORS.category.advertising,
    'Discounts': CHART_COLORS.category.discounts,
    'Returns': CHART_COLORS.category.returns,
    'Payment Fees': CHART_COLORS.category.fees,
    'Shipping': CHART_COLORS.category.shipping,
    'Other': CHART_COLORS.category.other,
};

export const ExpenseCategoryDonutChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.breakdown) return [];

        const categoryTotals: Record<string, number> = {};

        for (const item of finance.breakdown) {
            if (item.type === 'EXPENSE') {
                const rawCategory = item.category as string;
                const label = CATEGORY_LABEL_MAP[rawCategory] || 'Other';
                categoryTotals[label] = (categoryTotals[label] || 0) + Math.abs(Number(item.value) || 0);
            }
        }

        return Object.entries(categoryTotals)
            .map(([name, value]) => ({
                name,
                value: Math.round(value * 100) / 100,
                fill: CATEGORY_COLOR_MAP[name] || CHART_COLORS.category.other,
            }))
            .sort((a, b) => b.value - a.value);
    }, [finance?.breakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="50%"
                            outerRadius="80%"
                            paddingAngle={2}
                            strokeWidth={0}
                        >
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                        </Pie>
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            formatter={(value: number, name: string) => [
                                formatCompactNumber(value, currencySymbol),
                                t(`charts.expenseCategory.${name}`, name),
                            ]}
                        />
                        <Legend
                            formatter={(value: string) =>
                                t(`charts.expenseCategory.${value}`, value)
                            }
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
